"use client";

import React, { useCallback, useRef, useState } from "react";
import Image from "next/image";
import type { VideoFields } from "@/types/acf";
import { resolveMediaUrl } from "@/lib/resolveMediaUrl";

type Props = {
  data: VideoFields;
};

export default function Video({ data }: Props) {
  const [started, setStarted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const videoUrl = resolveMediaUrl(data?.video_mp4);
  const posterUrl = resolveMediaUrl(data?.poster);

  const handleStart = useCallback(() => {
    setError(null);
    setStarted(true);

    requestAnimationFrame(() => {
      videoRef.current?.play().catch(() => {
        setError("Video playback failed");
      });
    });
  }, []);

  const handleEnded = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setStarted(false);
  }, []);

  if (!data || data.hide) return null;
  if (!videoUrl) return null;

  return (
    <div id={data.id} className="min-w-0">
      {data.section_title && (
        <h2 className="text-center mb-20-to-40 text-title-2">
          {data.section_title}
        </h2>
      )}

      <div className="relative w-full aspect-video overflow-hidden bg-black">
        {!started && (
          <div className="absolute inset-0">
            {posterUrl ? (
              <Image
                src={posterUrl}
                alt="Video poster"
                fill
                className="object-cover select-none pointer-events-none"
                draggable={false}
                sizes="100vw"
              />
            ) : (
              <div className="h-full w-full bg-neutral-900" />
            )}

            <div className="absolute inset-0 flex items-center justify-center">
              <button
                type="button"
                onClick={handleStart}
                aria-label="Play video"
                className="
                  inline-flex items-center justify-center
                  w-20 h-20 rounded-full
                  bg-white/90 hover:bg-white
                  transition shadow-lg
                "
              >
                <svg
                  viewBox="0 0 24 24"
                  className="w-10 h-10 fill-black"
                  aria-hidden="true"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {started && (
          <video
            ref={videoRef}
            src={videoUrl}
            controls
            autoPlay
            playsInline
            className="h-full w-full object-cover"
            onEnded={handleEnded}
            onError={() => setError("Failed to load video")}
          />
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70 text-red-500">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
